import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { useDashboard } from '../../DashboardContext';
import { S } from '../../lib/constants';

/* ── Numeric safety: never let NaN/Infinity reach the screen ── */
const fin = (v, fallback = 0) => (Number.isFinite(v) ? v : fallback);

/* ── "Mo 28 (~2.3 yrs)" treatment for month-based milestones ── */
const monthLabel = (m) => {
  const n = fin(m, 0);
  if (!n || n <= 0) return 'Not reached';
  return `Mo ${n} (~${(n / 12).toFixed(1)} yrs)`;
};

export default function PlanVerdictSection() {
  const {
    activeStrategy,
    activeCalc,
    equivalentDCA,
    miningEconomics,
    sensitivityAnalysis,
    showAfterTaxEconomics,
    timeHorizon,
    fmt, fBTC,
  } = useDashboard();

  /* ── Empty state: no plan yet ── */
  if (!activeCalc?.monthlyData?.length) {
    return (
      <div className="print-deliverable rounded-lg p-10 text-center" style={S.card}>
        <div className="text-base font-semibold mb-1" style={{ color: '#1A1A1A' }}>
          No plan to evaluate yet
        </div>
        <div className="text-sm" style={{ color: '#737373' }}>
          Set up this plan's configuration to see the verdict
        </div>
      </div>
    );
  }

  const monthlyData = activeCalc.monthlyData;
  const strategyName = activeStrategy?.name || 'Current plan';

  /* ═════════ 1. Verdict computation ═════════ */
  let verdict = null;
  if (equivalentDCA) {
    const miningNAV = fin(equivalentDCA.miningNAV);
    const dcaValue = fin(equivalentDCA.dcaBtcValue);
    const spValue = fin(equivalentDCA.spValue);
    const bestAlt = Math.max(dcaValue, spValue);
    const altName = dcaValue >= spValue ? 'buying Bitcoin' : 'the S&P 500';
    const advantage = miningNAV - bestAlt;
    const pct = bestAlt > 0 ? advantage / bestAlt : (miningNAV > 0 ? 1 : 0);
    const taxSavings = fin(equivalentDCA.totalTaxSavings);

    let tier, accent, headline, support;
    if (pct > 0.15) {
      tier = 'Strong fit';
      accent = S.C.green;
      headline = `Mining is a strong fit, projected to outperform ${altName} by ${fmt(advantage)} over ${timeHorizon} years.`;
    } else if (pct >= 0) {
      tier = 'Moderate fit';
      accent = S.C.gold;
      headline = `Mining is a moderate fit, projected to edge out ${altName} by ${fmt(advantage)} over ${timeHorizon} years.`;
    } else {
      tier = 'Not a fit at current settings';
      accent = S.C.red;
      headline = `Mining is not a fit at current settings: ${altName === 'buying Bitcoin' ? 'simply buying Bitcoin' : 'the S&P 500'} is projected to come out ahead by ${fmt(Math.abs(advantage))}.`;
    }

    if (advantage > 0) {
      support = taxSavings > 0.25 * advantage
        ? `The biggest driver is tax treatment: ${fmt(taxSavings)} in deductions from equipment depreciation and operating expenses.`
        : 'The biggest driver is operational efficiency: this plan produces Bitcoin for less than it would cost to buy.';
    } else {
      support = 'Adjusting electricity cost, hardware pricing, or the time horizon can change this outcome.';
    }

    verdict = { tier, accent, headline, support, miningNAV, dcaValue, spValue, altName };
  }

  /* ═════════ 2. Three paths ═════════ */
  const paths = equivalentDCA ? [
    {
      title: 'Mine it',
      value: fin(equivalentDCA.miningNAV),
      color: S.C.gold,
      explainer: `What your ${fmt(fin(equivalentDCA.totalCashDeployed))} becomes running miners`,
      btcLine: `${fBTC(fin(equivalentDCA.miningTreasury), 2)} BTC accumulated`,
    },
    {
      title: 'Buy Bitcoin instead',
      value: fin(equivalentDCA.dcaBtcValue),
      color: S.C.blue,
      explainer: 'The same dollars buying BTC on the same schedule',
      btcLine: `${fBTC(fin(equivalentDCA.dcaBtcAccum), 2)} BTC accumulated`,
    },
    {
      title: 'S&P 500 instead',
      value: fin(equivalentDCA.spValue),
      color: S.C.green,
      explainer: 'The same dollars in a broad stock index fund',
      btcLine: null,
    },
  ] : null;
  const winnerIdx = paths
    ? paths.reduce((best, p, i) => (p.value > paths[best].value ? i : best), 0)
    : -1;

  /* ═════════ 3. Key moments ═════════ */
  const costPerBTC = showAfterTaxEconomics
    ? fin(equivalentDCA?.miningAfterTaxCostPerBTC, fin(miningEconomics?.netCostPerBTC))
    : fin(equivalentDCA?.miningCostPerBTC, fin(miningEconomics?.grossCostPerBTC));
  const avgSpot = fin(miningEconomics?.avgSpotPrice);
  const multiplier = fin(miningEconomics?.miningMultiple, fin(activeCalc.miningMultiplier));
  const keyMoments = [
    {
      // NAV vs out-of-pocket: counts the Bitcoin you still hold, marked to
      // market. Not "profit" — nothing has been realised.
      label: 'Position above water',
      value: monthLabel(miningEconomics?.postTaxBreakEvenMonth ?? activeCalc.breakEvenMonth),
      sub: showAfterTaxEconomics
        ? 'holdings worth more than spent, after write-offs'
        : 'holdings worth more than spent',
    },
    {
      // Cash actually received: revenue is Bitcoin SOLD, plus resale. A plan
      // that holds everything never pays back, by design — that is not a
      // failure and the label should not imply one.
      label: 'Cash returned',
      value: monthLabel(miningEconomics?.postTaxPaybackMonth ?? activeCalc.paybackMonth),
      sub: (activeStrategy?.sellPercent || 0) === 0 && activeStrategy?.initialOpexCoverage === 'otherIncome'
        ? 'never, by design: this plan holds every coin'
        : 'when cash received covers the outlay',
    },
    {
      label: 'Cost per BTC',
      value: costPerBTC > 0 ? fmt(costPerBTC) : '—',
      sub: avgSpot > 0 ? `market avg ${fmt(avgSpot)}` : (showAfterTaxEconomics ? 'after tax deductions' : 'before tax deductions'),
    },
    {
      label: 'Every $1 in',
      value: multiplier > 0 ? `$${multiplier.toFixed(2)} of BTC` : '—',
      sub: (miningEconomics?.taxSavings || 0) > 0 ? 'per dollar of true cost, after write-offs' : 'value produced per dollar spent',
    },
  ];

  /* ═════════ 4. What-if scenarios ═════════ */
  const scenarios = sensitivityAnalysis?.scenarios?.filter(s => Number.isFinite(s?.delta)) || [];
  const maxAbsDelta = Math.max(...scenarios.map(s => Math.abs(fin(s.delta))), 1);
  const profitFloor = fin(equivalentDCA?.totalCashDeployed, 0);
  const downside = scenarios.filter(s => fin(s.delta) < 0);
  const downsideSurvived = downside.filter(s => fin(s.nav) > profitFloor).length;

  /* ═════════ 5. NAV trajectory ticks ═════════ */
  const totalMonths = monthlyData[monthlyData.length - 1]?.month || monthlyData.length;
  const yearTicks = [];
  for (let y = 1; y <= Math.floor(totalMonths / 12); y++) yearTicks.push(y * 12);

  return (
    <div className="space-y-6">

      {/* ── 1. VERDICT BANNER ── */}
      <div
        className="print-deliverable rounded-lg p-6"
        style={{ ...S.card, borderLeft: `6px solid ${verdict ? verdict.accent : S.C.blue}` }}
      >
        <div style={S.labelSm} className="mb-3">
          Plan verdict · {strategyName} · {timeHorizon}-year horizon
        </div>
        {verdict ? (
          <>
            <div className="text-xs font-semibold mb-2" style={{ color: verdict.accent, textTransform: 'uppercase', letterSpacing: '1px' }}>
              {verdict.tier}
            </div>
            <div className="font-semibold" style={{ color: '#1A1A1A', fontSize: '26px', lineHeight: 1.3, maxWidth: '54em' }}>
              {verdict.headline}
            </div>
            <div className="text-sm mt-3" style={{ color: '#525252', maxWidth: '60em' }}>
              {verdict.support}
            </div>
          </>
        ) : (
          <>
            <div className="font-semibold" style={{ color: '#1A1A1A', fontSize: '26px', lineHeight: 1.3 }}>
              This plan is projected to grow to {fmt(fin(activeCalc.finalNAV))} over {timeHorizon} years.
            </div>
            <div className="text-sm mt-3" style={{ color: '#525252' }}>
              A side-by-side comparison against buying Bitcoin or the S&P 500 will appear here once benchmark data is available.
            </div>
          </>
        )}
      </div>

      {/* ── 2. THREE PATHS ── */}
      {paths && (
        <div className="print-deliverable">
          <div style={S.labelSm} className="mb-3">Same dollars, three paths</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {paths.map((p, i) => {
              const isWinner = i === winnerIdx;
              return (
                <div
                  key={p.title}
                  className="rounded-lg p-5"
                  style={{
                    background: '#FFFFFF',
                    borderRadius: '8px',
                    border: isWinner ? `2px solid ${p.color}` : '1px solid #D4D4D4',
                  }}
                >
                  <div className="flex items-baseline justify-between mb-3">
                    <div className="text-sm font-semibold" style={{ color: '#1A1A1A' }}>{p.title}</div>
                    {isWinner && (
                      <div style={{ ...S.labelSm, color: p.color }}>Best outcome</div>
                    )}
                  </div>
                  <div className="font-bold mb-2" style={{ ...S.tn('#1A1A1A'), fontSize: '32px', lineHeight: 1.1 }}>
                    {fmt(p.value)}
                  </div>
                  <div className="text-xs mb-3" style={{ color: '#737373' }}>{p.explainer}</div>
                  <div className="pt-3 text-xs" style={{ borderTop: '1px solid #F5F5F5' }}>
                    {p.btcLine
                      ? <span style={S.tn(p.color)}>{p.btcLine}</span>
                      : <span style={{ color: '#A3A3A3' }}>No Bitcoin held</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── 3. KEY MOMENTS ── */}
      <div className="print-deliverable rounded-lg p-5" style={S.card}>
        <div style={S.labelSm} className="mb-4">Key moments in this plan</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {keyMoments.map((m) => (
            <div key={m.label} className="rounded p-4" style={S.innerCard}>
              <div style={S.labelSm} className="mb-2">{m.label}</div>
              <div className="font-bold" style={{ ...S.tn('#1A1A1A'), fontSize: '18px' }}>{m.value}</div>
              <div className="text-xs mt-1" style={{ color: '#737373' }}>{m.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── 4. WHAT-IF ── */}
      {scenarios.length > 0 && (
        <div className="print-deliverable rounded-lg p-5" style={S.card}>
          <div style={S.labelSm} className="mb-1">If conditions change</div>
          <div className="text-xs mb-4" style={{ color: '#737373' }}>
            How your final net worth moves if the market shifts for or against this plan
          </div>
          <div className="space-y-2">
            {scenarios.map((s, i) => {
              const delta = fin(s.delta);
              const width = Math.min(Math.abs(delta) / maxAbsDelta * 100, 100);
              const positive = delta >= 0;
              return (
                <div key={i} className="flex items-center gap-3">
                  <div className="text-xs flex-shrink-0" style={{ color: '#525252', width: '160px' }}>
                    {s.label}
                  </div>
                  <div className="flex-1 flex items-center" style={{ height: '14px' }}>
                    <div className="flex justify-end" style={{ width: '50%' }}>
                      {!positive && (
                        <div style={{ width: `${width}%`, height: '10px', background: S.C.red, borderRadius: '2px 0 0 2px' }} />
                      )}
                    </div>
                    <div style={{ width: '1px', height: '14px', background: '#A3A3A3', flexShrink: 0 }} />
                    <div style={{ width: '50%' }}>
                      {positive && (
                        <div style={{ width: `${width}%`, height: '10px', background: S.C.green, borderRadius: '0 2px 2px 0' }} />
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-right flex-shrink-0" style={{ ...S.tn(positive ? S.C.green : S.C.red), width: '110px' }}>
                    {positive ? '+' : '−'}{fmt(Math.abs(delta))}
                  </div>
                </div>
              );
            })}
          </div>
          {downside.length > 0 && (
            <div className="mt-4 pt-3 text-xs" style={{ borderTop: '1px solid #F5F5F5', color: '#525252' }}>
              Your plan stays profitable in {downsideSurvived} of {downside.length} downside scenarios tested.
            </div>
          )}
        </div>
      )}

      {/* ── 5. NAV TRAJECTORY ── */}
      <div className="print-deliverable rounded-lg p-5" style={S.card}>
        <div className="mb-4">
          <h3 className="font-semibold text-sm" style={{ color: '#1A1A1A' }}>
            Your projected net worth from this plan
          </h3>
          <p className="text-xs mt-0.5" style={{ color: '#737373' }}>
            {strategyName}: Bitcoin holdings plus cash, month by month
            {activeCalc.fleetOfflineMonth > 0 && activeCalc.fleetOfflineMonth < totalMonths &&
              ` · mining ends Yr ${Math.ceil(activeCalc.fleetOfflineMonth / 12)}; growth after that is Bitcoin appreciation only`}
          </p>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={monthlyData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
            <CartesianGrid vertical={false} strokeDasharray="3 6" stroke="#E5E5E5" />
            <XAxis
              dataKey="month"
              stroke="transparent"
              tick={{ fontSize: 11, fill: '#737373' }}
              ticks={yearTicks.length ? yearTicks : undefined}
              tickFormatter={(m) => (m % 12 === 0 ? `Yr ${m / 12}` : `Mo ${m}`)}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              stroke="transparent"
              tick={{ fontSize: 11, fill: '#737373' }}
              tickFormatter={(v) => fmt(v)}
              axisLine={false}
              tickLine={false}
              width={70}
            />
            <Tooltip
              contentStyle={S.ttip}
              formatter={(v) => [fmt(fin(v)), 'Net worth']}
              labelFormatter={(m) => `Month ${m}`}
            />
            {activeCalc.fleetOfflineMonth > 0 && activeCalc.fleetOfflineMonth < totalMonths && (
              <ReferenceLine
                x={activeCalc.fleetOfflineMonth}
                stroke="#92700C" strokeDasharray="4 4"
                label={{ value: 'Mining ends', position: 'insideTopRight', fill: '#92700C', fontSize: 11, fontWeight: 600 }}
              />
            )}
            <Area
              type="monotone"
              dataKey="nav"
              stroke={S.C.blue}
              strokeWidth={2}
              fill={S.C.blue}
              fillOpacity={0.06}
              dot={false}
              activeDot={{ r: 4, fill: '#fff', stroke: S.C.blue, strokeWidth: 2.5 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
